import { jsx as _jsx } from "react/jsx-runtime";
import MuiLinearProgress from '@mui/material/LinearProgress';
import { GridLogicOperator } from '@mui/x-data-grid-pro';
import { debounce } from 'essential-utils';
import * as React from 'react';
import { useMounted } from '../../hooks';
import { deepMerge } from '../../utils';
import { usePageSize } from './use-page-size';
export function useInfiniteLoadingGridProps(props) {
    const { infiniteLoading, onFetchRows, slots: _slots, slotProps: _slotProps, sortModel = [], filterModel = { items: [] }, fetchRowsData } = props;
    const mounted = useMounted();
    const [loading, setLoading] = React.useState(false);
    const [rows, setRows] = React.useState(props.rows ?? []);
    const [hasMoreRows, setHasMoreRows] = React.useState(true);
    const appendRows = React.useRef(false);
    const { pageSize, gridHeight } = usePageSize(props.apiRef, props.initialState, props.rowHeight);
    if (infiniteLoading && !onFetchRows) {
        throw new Error('onFetchRows is required with InfiniteLoading Data Grid');
    }
    const fetchDataDebounced = React.useMemo(() => debounce(async (params) => {
        if (!mounted.current) {
            return;
        }
        setLoading(true);
        const newRows = await onFetchRows?.(params);
        if (mounted.current) {
            setLoading(false);
            if (!newRows || newRows.length < pageSize) {
                setHasMoreRows(false);
            }
            else {
                setHasMoreRows(true);
            }
        }
    }, 0), 
    // intentionally exclude onFetchRows
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mounted, pageSize]);
    React.useEffect(() => {
        return () => {
            fetchDataDebounced.cancel();
        };
    }, [fetchDataDebounced]);
    const onRowsScrollEnd = () => {
        const scrollTop = props.apiRef?.current?.getScrollPosition?.().top;
        if (loading || scrollTop === 0 || !hasMoreRows) {
            return;
        }
        appendRows.current = true;
        void fetchDataDebounced({
            firstRowToRender: rows.length,
            lastRowToRender: rows.length + pageSize,
            sortModel,
            filterModel,
            data: fetchRowsData
        });
    };
    const slots = deepMerge({}, _slots ?? {}, {
        ...(rows.length !== 0
            ? { loadingOverlay: () => _jsx(MuiLinearProgress, {}) }
            : {})
    });
    const slotProps = deepMerge({}, _slotProps ?? {}, {
        filterPanel: {
            logicOperators: [GridLogicOperator.And]
        }
    });
    React.useEffect(() => {
        if (infiniteLoading && pageSize && gridHeight && !loading) {
            appendRows.current = false;
            void fetchDataDebounced({
                firstRowToRender: 0,
                lastRowToRender: pageSize,
                sortModel,
                filterModel,
                data: fetchRowsData
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchRowsData, filterModel, sortModel, pageSize, infiniteLoading]);
    const rowsHash = infiniteLoading ? serializeRows(props.rows ?? []) : '';
    React.useEffect(() => {
        if (infiniteLoading) {
            if (!mounted.current) {
                return;
            }
            if (appendRows.current) {
                setRows((prevRows) => [
                    ...prevRows,
                    ...(props.rows ?? []).filter((row) => !rows.some((r) => r.databaseId === row.databaseId))
                ]);
            }
            else {
                props.apiRef?.current?.scrollToIndexes({ rowIndex: 0 });
                setRows(props.rows ?? []);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowsHash, infiniteLoading]);
    if (!props.infiniteLoading) {
        return {};
    }
    return {
        rows,
        slots,
        slotProps,
        loading,
        onRowsScrollEnd,
        scrollEndThreshold: gridHeight ?? 450,
        filterMode: 'server',
        sortingMode: 'server',
        onSortModelChange: (model, details) => {
            if (mounted.current) {
                setRows([]);
            }
            props.onSortModelChange?.(model, details);
        },
        onFilterModelChange: (model, details) => {
            if (mounted.current) {
                setRows([]);
            }
            props.onFilterModelChange?.(model, details);
        }
    };
}
function serializeRows(rows) {
    const rowIds = [
        rows.map((row) => row.databaseId ?? row.id).join(','),
        rows.filterModel?.items
            .map((item) => `${item.field}:${item.value}`)
            .join(','),
        rows.filterModel?.quickFilterValues?.join(','),
        rows.sortModel?.map((sort) => `${sort.field}:${sort.sort}`).join(',')
    ];
    return rowIds.filter(Boolean).join('|');
}
